<template>
  <div class="qr-page">
    <div class="qr-page-inner container-fluid py-4">
      <div class="qr-canvas">
        <!-- Top title + tips -->
        <header class="bp-header mb-3">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 class="bp-title mb-1">Results · Task Results & Progress</h1>
              <p class="bp-subtitle mb-0">
                View logs, analysis progress, and image results for this batch task in real time.
              </p>
            </div>

            <!-- Current task ID -->
            <div class="qr-taskbox d-flex flex-column align-items-end">
              <div class="qr-taskbox-label">Current Task ID</div>
              <div class="d-flex align-items-center gap-2 mt-1">
                <code class="qr-taskbox-id">
                  {{ taskId || 'Task ID not available' }}
                </code>

                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="copyTaskId"
                  :disabled="!taskId"
                >
                  Copy ID
                </button>

                <button
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  @click="downloadResults"
                  :disabled="!taskId || downloadLoading"
                >
                  <span v-if="downloadLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  Download Results
                </button>
              </div>

              <div class="bp-card-caption mt-1 text-end">
                We recommend saving this ID so you can reopen the results page later.
              </div>
            </div>
          </div>

          <!-- Info banner -->
          <div class="qr-info-banner mt-3">
            <div class="qr-info-dot"></div>
            <div class="flex-grow-1">
              <div class="qr-info-title">
                The task is running in the background. You may close this page at any time.
              </div>
              <div class="qr-info-text">
                The background task will not be interrupted by closing the browser.
                Later, simply enter
                <strong>Results / Query Page</strong>
                with
                <code>{{ taskId || 'task_id' }}</code>
                to return to this page and view logs, progress, and results.
              </div>
            </div>
          </div>
        </header>

        <!-- Execution logs -->
        <section class="bp-card mb-4">
          <div class="bp-card-header d-flex justify-content-between align-items-center">
            <div>
              <div class="bp-card-title-sm">Execution Logs</div>
              <div class="bp-card-caption">
                Displays standard output logs from the backend execution process for troubleshooting.
              </div>
            </div>
            <button @click="refreshLogs" class="btn btn-sm btn-outline-secondary">
              Refresh Logs
            </button>
          </div>

          <div class="bp-card-body">
            <div class="qr-log-wrapper">
              <pre v-if="logs || logs === ''" class="qr-log-view">{{ logs || 'No logs available' }}</pre>
            </div>
          </div>
        </section>

        <!-- Analysis progress -->
        <section class="bp-card mb-4">
          <div class="bp-card-header d-flex justify-content-between align-items-center">
            <div>
              <div class="bp-card-title-sm">Analysis Progress</div>
              <div class="bp-card-caption">
                View the execution status of the current task by step, with support for multi-sample comparison.
              </div>
            </div>
            <button @click="refreshProgress" class="btn btn-sm btn-outline-primary">
              Refresh Results
            </button>
          </div>

          <div class="bp-card-body">
            <el-table
              class="qr-progress-table"
              :data="progressData"
              style="width: 100%"
              border
              :height="400"
            >
              <el-table-column
                prop="name"
                label="Item"
                width="160"
                fixed="left"
              />
              <el-table-column
                v-for="col in progressColumns.filter(c => c !== 'name')"
                :key="col"
                :prop="col"
                :label="''"
                min-width="140"
              />
            </el-table>
          </div>
        </section>

        <!-- Image results -->
        <section class="bp-card mb-4">
          <div class="bp-card-header d-flex justify-content-between align-items-center">
            <div>
              <div class="bp-card-title-sm">Image Results</div>
              <div class="bp-card-caption">
                Result images are grouped by sample ID and subdirectory for quick quality control and browsing.
              </div>
            </div>
            <button @click="refreshImages" class="btn btn-sm btn-outline-success">
              Refresh Images
            </button>
          </div>

          <div class="bp-card-body">
            <div
              v-if="!samples || samples.length === 0"
              class="bp-empty text-center py-4"
            >
              <p class="mb-1">No image results available</p>
              <p class="bp-card-caption mb-0">
                The task may still be running, or no image files have been generated yet.
                Please refresh again later.
              </p>
            </div>

            <div v-else id="image-results" class="qr-image-results">
              <div
                v-for="sample in samples"
                :key="sample.sample_id"
                class="qr-sample-block"
              >
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h3 class="qr-sample-title mb-0">
                    Sample: <span>{{ sample.sample_id }}</span>
                  </h3>
                </div>

                <div class="qr-sample-groups">
                  <div
                    v-for="group in sample.images"
                    :key="group.sub_folder"
                    class="qr-group-block"
                  >
                    <div class="qr-group-header">
                      <h4 class="qr-group-title">
                        {{ group.sub_folder || 'Default Directory' }}
                      </h4>
                    </div>

                    <div class="qr-group-images">
                      <div
                        v-for="imageUrl in group.images"
                        :key="imageUrl"
                        class="qr-image-wrapper"
                      >
                        <img
                          :src="imageUrl"
                          :alt="'Image from ' + sample.sample_id + ' - ' + group.sub_folder"
                          class="qr-image"
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <div class="py-2"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import API_BASE_URL from '@/api';
import { ElTable, ElTableColumn } from 'element-plus';
import { debounce } from 'lodash';
import { ElMessage } from 'element-plus';

export default {
  name: 'QueryPage',
  components: {
    ElTable,
    ElTableColumn,
  },
  computed: {
    apiBaseUrl() {
      return API_BASE_URL;
    },
  },
  data() {
    return {
      downloadLoading: false,
      taskId: '',
      logs: null,
      progressColumns: [],
      progressData: [],
      images: [],
      samples: [],
    };
  },
  methods: {
   
downloadResults: debounce(async function () {
  if (!this.taskId || this.downloadLoading) return;

  try {
    this.downloadLoading = true;

    const resp = await axios.post(
      `${API_BASE_URL}/api/common/download_results_status/`,
      { task_id: this.taskId }
    );

    const { status, download_url } = resp.data || {};
    const s = (status || '').toLowerCase();

    if (s === 'success' || s === 'failed') {
      if (!download_url) {
        ElMessage.warning('Download URL not available.');
        return;
      }
      // 关键：交给浏览器 / nginx / caddy
      window.location.href = download_url;
      return;
    }

    if (s === 'running') {
      ElMessage.info('Task is still running.');
      return;
    }

    // unknown / 其它
    ElMessage.info('Results are not ready.');
  } catch (e) {
    console.error('downloadResults error:', e);
    ElMessage.error('Failed to check download status.');
  } finally {
    this.downloadLoading = false;
  }
}, 300),



    

    copyTaskId() {
      if (!this.taskId) return;
      const text = this.taskId;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => alert('Task ID copied to clipboard.'))
          .catch(() => alert('Copy failed. Please select the Task ID and copy it manually.'));
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Task ID copied to clipboard.');
      } catch (e) {
        alert('Copy failed. Please select the Task ID and copy it manually.');
      } finally {
        document.body.removeChild(textarea);
      }
    },

    refreshProgress: debounce(async function () {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/common/progress/`, {
          task_id: this.taskId,
        });

        const raw = response.data.table_data;
        if (!raw) return;

        const colCount = raw[Object.keys(raw)[0]]?.length || 0;
        const columnNames = Array.from({ length: colCount }, (_, i) => String(i));
        const orderedKeys = [
          'Step',
          'Params',
          ...Object.keys(raw).filter(k => k !== 'Step' && k !== 'Params').sort(),
        ];

        const transposed = orderedKeys.map((key) => {
          const row = { name: key };
          (raw[key] || []).forEach((val, i) => {
            row[i] = val;
          });
          return row;
        });

        this.progressData = transposed;
        this.progressColumns = ['name', ...columnNames];
      } catch (error) {
        console.error('Error fetching progress data:', error);
      }
    }, 300),

    refreshLogs: debounce(function () {
      try {
        axios.post(`${API_BASE_URL}/api/common/fetchLogs/`, { task_id: this.taskId })
          .then(response => {
            this.logs = response.data.logs;
          });
      } catch (error) {
        console.error('Error fetching logs:', error);
      }
    }, 300),

    refreshImages: debounce(async function () {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/common/fetchImages/`, {
          task_id: this.taskId,
        });
        this.samples = response.data.samples || [];
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    }, 300),
  },

  mounted() {
    this.taskId = this.$route.params.taskId;
    // If needed, uncomment:
    // this.refreshLogs();
    // this.refreshProgress();
    // this.refreshImages();
  },
};
</script>

<style scoped>
/* =========================
   Outer: same background family as HomePage
   ========================= */
.qr-page{
  min-height: calc(100vh - 88px);
  color: rgba(15, 23, 42, 0.92);
  padding: 0;
  background: transparent;
}

.qr-page-inner{
  max-width: 1280px;
  margin: 0 auto;
}

/* =========================
   Canvas: foggy white + rounded container
   ========================= */
.qr-canvas{
  background:
    radial-gradient(900px 420px at 18% 0%, rgba(13,110,253,0.10), transparent 60%),
    radial-gradient(820px 420px at 82% 8%, rgba(102,16,242,0.08), transparent 58%),
    linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.42) 100%);

  border: 1px solid rgba(15,23,42,0.06);
  border-radius: 18px;
  box-shadow: 0 16px 50px rgba(15,23,42,0.06);
  padding: 14px 12px;
}

/* =========================
   Top header card
   ========================= */
.bp-header{
  padding: 1rem 1.15rem 1.05rem;
  border-radius: 16px;
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.04);
}

.bp-title{
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(15,23,42,0.92);
  letter-spacing: -0.01em;
}
.bp-subtitle{
  font-size: 0.86rem;
  color: rgba(15,23,42,0.62);
}

/* Right task ID */
.qr-taskbox-label{
  font-size: 0.78rem;
  color: rgba(15,23,42,0.55);
}
.qr-taskbox-id{
  font-size: 0.8rem;
  padding: 0.12rem 0.7rem;
  border-radius: 999px;
  background: rgba(15,23,42,0.05);
  color: rgba(15,23,42,0.92);
  border: 1px solid rgba(15,23,42,0.10);
}

/* Banner */
.qr-info-banner{
  display:flex;
  align-items:flex-start;
  gap: .75rem;
  margin-top: .9rem;
  padding: .9rem 1rem;
  border-radius: 12px;
  background: rgba(13,110,253,0.07);
  border: 1px solid rgba(13,110,253,0.16);
}
.qr-info-dot{
  width:10px;
  height:10px;
  border-radius: 999px;
  margin-top: .25rem;
  background: #22c55e;
}
.qr-info-title{
  font-size: .92rem;
  font-weight: 700;
  color: rgba(15,23,42,0.90);
  margin-bottom: .1rem;
}
.qr-info-text{
  font-size: .80rem;
  color: rgba(15,23,42,0.68);
}

/* =========================
   Common card
   ========================= */
.bp-card{
  border-radius: 16px;
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.03);
}

.bp-card-header{
  padding: 0.85rem 1.1rem 0.75rem;
  border-bottom: 1px solid rgba(15,23,42,0.06);
}
.bp-card-body{
  padding: 0.9rem 1.1rem 1rem;
}
.bp-card-title-sm{
  font-size: 0.92rem;
  font-weight: 700;
  color: rgba(15,23,42,0.92);
}
.bp-card-caption{
  font-size: 0.78rem;
  color: rgba(15,23,42,0.60);
}

.bp-empty{
  padding: 1.5rem 0;
  color: rgba(15,23,42,0.60);
}

/* Logs */
.qr-log-wrapper{
  height: 360px;
  overflow-y: auto;
  background-color: rgba(255,255,255,0.86);
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(15,23,42,0.10);
}
.qr-log-view{
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.78rem;
  color: rgba(15,23,42,0.92);
  white-space: pre-wrap;
}

/* element-plus table header */
.qr-progress-table :deep(.el-table__header-wrapper th){
  background-color: rgba(255,255,255,0.88);
  font-weight: 700;
  font-size: 0.8rem;
  color: rgba(15,23,42,0.78);
}
.qr-progress-table :deep(.el-table__body-wrapper td){
  font-size: 0.8rem;
}

/* Image results */
.qr-image-results{
  display:flex;
  flex-direction: column;
  gap: 1.6rem;
}
.qr-sample-block{
  padding: 0.9rem 0.95rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.86);
}
.qr-sample-title{
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(15,23,42,0.90);
}
.qr-sample-title span{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.82rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: rgba(15,23,42,0.06);
  border: 1px solid rgba(15,23,42,0.10);
}

.qr-sample-groups{
  display:flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.6rem;
}

.qr-group-title{
  font-size: 0.82rem;
  font-weight: 700;
  color: rgba(15,23,42,0.66);
  margin-bottom: 0.25rem;
}

.qr-group-images{
  display:flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.qr-image-wrapper{
  flex: 0 0 calc(25% - 0.75rem);
  max-width: 220px;
}
.qr-image{
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.92);
  object-fit: contain;
  transition: transform .16s ease, box-shadow .16s ease;
}
.qr-image:hover{
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
}

/* Button pill style */
.qr-taskbox .btn{
  border-radius: 999px;
}
.qr-taskbox .btn .spinner-border{
  vertical-align: -0.12em;
}

/* responsive */
@media (max-width: 992px){
  .qr-image-wrapper{ flex: 0 0 calc(50% - 0.75rem); }
}
@media (max-width: 576px){
  .qr-image-wrapper{ flex: 0 0 100%; }
  .qr-taskbox{ align-items: flex-start !important; }
  .qr-taskbox-id{
    max-width: 220px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .qr-canvas{
    padding: 12px 10px;
    border-radius: 16px;
  }
}
</style>

